import { logger } from './logger';

// Tipos para validação
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedValue?: any;
}

// Sanitização de strings
export const sanitizeString = (input: any): string => {
  if (typeof input !== 'string') {
    return String(input || '').trim();
  }

  return input
    .trim()
    .replace(/[<>]/g, '') // Remove caracteres HTML básicos
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+=/gi, '') // Remove event handlers
    .substring(0, 1000); // Limita tamanho
};

// Sanitização de números
export const sanitizeNumber = (input: any): number => {
  const num = parseFloat(input);
  if (isNaN(num) || !isFinite(num)) {
    return 0;
  }
  // Limita valores extremos
  return Math.max(-999999999, Math.min(999999999, num));
};

// Validação de email
export const validateEmail = (email: string): ValidationResult => {
  const sanitized = sanitizeString(email);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const errors: string[] = [];

  if (!sanitized) {
    errors.push('Email é obrigatório');
  } else if (!emailRegex.test(sanitized)) {
    errors.push('Formato de email inválido');
  } else if (sanitized.length > 254) {
    errors.push('Email muito longo');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: sanitized.toLowerCase()
  };
};

// Validação de nome/título
export const validateName = (name: string, fieldName: string = 'Nome'): ValidationResult => {
  const sanitized = sanitizeString(name);
  const errors: string[] = [];

  if (!sanitized) {
    errors.push(`${fieldName} é obrigatório`);
  } else if (sanitized.length < 2) {
    errors.push(`${fieldName} deve ter pelo menos 2 caracteres`);
  } else if (sanitized.length > 100) {
    errors.push(`${fieldName} deve ter no máximo 100 caracteres`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: sanitized
  };
};

// Validação de valor monetário
export const validateAmount = (amount: any): ValidationResult => {
  const sanitized = sanitizeNumber(amount);
  const errors: string[] = [];

  if (sanitized < 0) {
    errors.push('Valor não pode ser negativo');
  } else if (sanitized > 999999) {
    errors.push('Valor muito alto');
  } else if (sanitized === 0) {
    errors.push('Valor deve ser maior que zero');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: Math.round(sanitized * 100) / 100 // Arredonda para 2 casas decimais
  };
};

// Validação de data
export const validateDate = (date: any): ValidationResult => {
  const errors: string[] = [];
  let sanitizedDate: Date;

  try {
    if (date instanceof Date) {
      sanitizedDate = date;
    } else if (typeof date === 'string' || typeof date === 'number') {
      sanitizedDate = new Date(date);
    } else {
      errors.push('Formato de data inválido');
      return { isValid: false, errors };
    }

    if (isNaN(sanitizedDate.getTime())) {
      errors.push('Data inválida');
    } else {
      const now = new Date();
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

      if (sanitizedDate < oneYearAgo) {
        errors.push('Data muito antiga');
      } else if (sanitizedDate > oneYearFromNow) {
        errors.push('Data muito futura');
      }
    }
  } catch (error) {
    logger.error('Error validating date', { error, date });
    errors.push('Erro ao validar data');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: sanitizedDate
  };
};

// Validação de participante
export const validateParticipante = (participante: any): ValidationResult => {
  const errors: string[] = [];
  const sanitized: any = {};

  // Validar nome
  const nameValidation = validateName(participante?.nome, 'Nome do participante');
  if (!nameValidation.isValid) {
    errors.push(...nameValidation.errors);
  } else {
    sanitized.nome = nameValidation.sanitizedValue;
  }

  // Validar email (opcional)
  if (participante?.email) {
    const emailValidation = validateEmail(participante.email);
    if (!emailValidation.isValid) {
      errors.push(...emailValidation.errors);
    } else {
      sanitized.email = emailValidation.sanitizedValue;
    }
  }

  // Validar ID (se fornecido)
  if (participante?.id) {
    sanitized.id = sanitizeString(participante.id);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: sanitized
  };
};

// Validação de despesa
export const validateDespesa = (despesa: any): ValidationResult => {
  const errors: string[] = [];
  const sanitized: any = {};

  // Validar título
  const titleValidation = validateName(despesa?.titulo, 'Título da despesa');
  if (!titleValidation.isValid) {
    errors.push(...titleValidation.errors);
  } else {
    sanitized.titulo = titleValidation.sanitizedValue;
  }

  // Validar valor
  const amountValidation = validateAmount(despesa?.valor);
  if (!amountValidation.isValid) {
    errors.push(...amountValidation.errors);
  } else {
    sanitized.valor = amountValidation.sanitizedValue;
  }

  // Validar data
  const dateValidation = validateDate(despesa?.data);
  if (!dateValidation.isValid) {
    errors.push(...dateValidation.errors);
  } else {
    sanitized.data = dateValidation.sanitizedValue;
  }

  // Validar categoria
  if (despesa?.categoria) {
    sanitized.categoria = sanitizeString(despesa.categoria);
  }

  // Validar descrição (opcional)
  if (despesa?.descricao) {
    sanitized.descricao = sanitizeString(despesa.descricao);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: sanitized
  };
};

// Validação de racha
export const validateRacha = (racha: any, partial: boolean = false): ValidationResult => {
  const errors: string[] = [];
  const sanitized: any = {};

  // Validar nome (titulo -> nome)
  if (!partial || racha?.nome !== undefined) {
    const nameValidation = validateName(racha?.nome, 'Nome do racha');
    if (!nameValidation.isValid) {
      errors.push(...nameValidation.errors);
    } else {
      sanitized.nome = nameValidation.sanitizedValue;
    }
  }

  // Validar participantes
  if (racha?.participantes !== undefined) {
    if (!Array.isArray(racha.participantes)) {
      errors.push('Lista de participantes inválida');
    } else {
      const participantesValidados = [];
      for (const participante of racha.participantes) {
        const participanteValidation = validateParticipante(participante);
        if (!participanteValidation.isValid) {
          errors.push(...participanteValidation.errors);
        } else {
          participantesValidados.push(participanteValidation.sanitizedValue);
        }
      }
      sanitized.participantes = participantesValidados;
    }
  }

  // Preserve status field (for finalize/reactivate)
  if (racha?.status !== undefined) {
    const validStatuses = ['ativo', 'finalizado'];
    if (validStatuses.includes(racha.status)) {
      sanitized.status = racha.status;
    } else {
      errors.push('Status inválido');
    }
  }

  // Preserve ultimaAtividade field
  if (racha?.ultimaAtividade !== undefined) {
    sanitized.ultimaAtividade = sanitizeString(racha.ultimaAtividade);
  }

  // Preserve dataFinalizacao field
  if (racha?.dataFinalizacao !== undefined) {
    sanitized.dataFinalizacao = racha.dataFinalizacao;
  }

  // Preserve emoji field
  if (racha?.emoji !== undefined) {
    sanitized.emoji = sanitizeString(racha.emoji);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: sanitized
  };
};

// Validação de grupo
export const validateGrupo = (grupo: any, partial: boolean = false): ValidationResult => {
  const errors: string[] = [];
  const sanitized: any = {};

  // Validar nome
  if (!partial || grupo?.nome !== undefined) {
    const nameValidation = validateName(grupo?.nome, 'Nome do grupo');
    if (!nameValidation.isValid) {
      errors.push(...nameValidation.errors);
    } else {
      sanitized.nome = nameValidation.sanitizedValue;
    }
  }

  // Validar emoji (opcional)
  if (grupo?.emoji) {
    sanitized.emoji = sanitizeString(grupo.emoji);
  } else {
    sanitized.emoji = '';
  }

  // Validar criadorId (se fornecido)
  if (grupo?.criadorId) {
    sanitized.criadorId = sanitizeString(grupo.criadorId);
  }

  // Validar participantes
  if (grupo?.participantes !== undefined && !Array.isArray(grupo.participantes)) {
    errors.push('Lista de participantes inválida');
  } else if (grupo?.participantes) {
    const participantesValidados = [];
    for (const participante of grupo.participantes) {
      const participanteValidation = validateParticipante(participante);
      if (!participanteValidation.isValid) {
        errors.push(...participanteValidation.errors);
      } else {
        participantesValidados.push(participanteValidation.sanitizedValue);
      }
    }
    sanitized.participantes = participantesValidados;
  } else {
    // Se não fornecido, não define para não sobrescrever com undefined em updates parciais
    // Mas se for criação, o service deve garantir que existe se for obrigatório
  }

  // Validar booleanos
  if (grupo?.incluirCriador !== undefined) {
    sanitized.incluirCriador = Boolean(grupo.incluirCriador);
  }

  if (grupo?.dataCriacao) {
    const dateValidation = validateDate(grupo.dataCriacao);
    if (dateValidation.isValid) {
      sanitized.dataCriacao = dateValidation.sanitizedValue;
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedValue: sanitized
  };
};

// Função utilitária para validar e sanitizar qualquer objeto
export const validateAndSanitize = (data: any, validationType: string): ValidationResult => {
  try {
    switch (validationType) {
      case 'email':
        return validateEmail(data);
      case 'name':
        return validateName(data);
      case 'amount':
        return validateAmount(data);
      case 'date':
        return validateDate(data);
      case 'participante':
        return validateParticipante(data);
      case 'despesa':
        return validateDespesa(data);
      case 'racha':
        return validateRacha(data);
      case 'grupo':
        return validateGrupo(data);
      default:
        logger.warn('Unknown validation type', { validationType });
        return {
          isValid: false,
          errors: ['Tipo de validação desconhecido']
        };
    }
  } catch (error) {
    logger.error('Error in validation', { error, validationType, data });
    return {
      isValid: false,
      errors: ['Erro interno de validação']
    };
  }
};